import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

// Import configs and util modules
import appSettings from '../../config/settings';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  autocompletePlaceSelected,
  autocompleteSearchRequired,
  composeValidators,
  moneySubUnitAmountAtLeast,
  required,
  validateInteger,
} from '../../util/validators';

// Import shared components
import {
  Button,
  FieldCheckbox,
  FieldCurrencyInput,
  FieldLocationAutocompleteInput,
  FieldTextInput,
  Form,
  ValidationError,
} from '../../components';

// Import modules from this directory
import css from './FulfilmentSettingsForm.module.css';

const identity = v => v;

/**
 * Form for default provider fulfilment settings.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const FulfilmentSettingsForm = props => (
  <FinalForm
    {...props}
    validate={values => {
      const errors = {};
      const deliveryOptions = values.deliveryOptions || [];
      const farmCollectionEnabled = deliveryOptions.includes('farmCollection');
      const localDeliveryEnabled = deliveryOptions.includes('localDelivery');
      const courierDeliveryEnabled = deliveryOptions.includes('courierDelivery');

      if (farmCollectionEnabled && !values.location?.search) {
        errors.location = 'FulfilmentSettingsForm.addressRequired';
      }
      if (deliveryOptions.length === 0) {
        errors.deliveryOptions = 'FulfilmentSettingsForm.deliveryOptionsRequired';
      }
      if (localDeliveryEnabled && !values.localDeliveryFeeInSubunits) {
        errors.localDeliveryFeeInSubunits = 'FulfilmentSettingsForm.localDeliveryFeeRequired';
      }
      if (localDeliveryEnabled && values.localDeliveryRadiusInMiles == null) {
        errors.localDeliveryRadiusInMiles = 'FulfilmentSettingsForm.localDeliveryRadiusRequired';
      }
      if (courierDeliveryEnabled && !values.courierDeliveryFeeInSubunits) {
        errors.courierDeliveryFeeInSubunits = 'FulfilmentSettingsForm.courierDeliveryFeeRequired';
      }
      if (values.freeDeliveryEnabled === true && !values.freeDeliveryThreshold) {
        errors.freeDeliveryThreshold = 'FulfilmentSettingsForm.freeDeliveryThresholdRequired';
      }

      return errors;
    }}
    render={formRenderProps => {
      const {
        formId = 'FulfilmentSettingsForm',
        className,
        disabled,
        errors,
        handleSubmit,
        invalid,
        inProgress,
        marketplaceCurrency,
        pristine,
        ready,
        saveError,
        saveActionMsg,
        submitFailed,
        values,
      } = formRenderProps;
      const intl = useIntl();

      const optionalText = intl.formatMessage({
        id: 'FulfilmentSettingsForm.optionalText',
      });
      const addressNotRecognizedMessage = intl.formatMessage({
        id: 'FulfilmentSettingsForm.addressNotRecognized',
      });
      const addressRequiredMessage = intl.formatMessage({
        id: 'FulfilmentSettingsForm.addressRequired',
      });
      const deliveryOptionsRequiredMessage = intl.formatMessage({
        id: 'FulfilmentSettingsForm.deliveryOptionsRequired',
      });
      const localDeliveryFeeRequired = intl.formatMessage({
        id: 'FulfilmentSettingsForm.localDeliveryFeeRequired',
      });
      const localDeliveryRadiusRequired = intl.formatMessage({
        id: 'FulfilmentSettingsForm.localDeliveryRadiusRequired',
      });
      const localDeliveryRadiusMin = intl.formatMessage(
        { id: 'FulfilmentSettingsForm.localDeliveryRadiusMin' },
        { min: 0 }
      );
      const localDeliveryRadiusMax = intl.formatMessage(
        { id: 'FulfilmentSettingsForm.localDeliveryRadiusMax' },
        { max: 1100 }
      );
      const courierDeliveryFeeRequired = intl.formatMessage({
        id: 'FulfilmentSettingsForm.courierDeliveryFeeRequired',
      });
      const freeDeliveryThresholdRequired = intl.formatMessage({
        id: 'FulfilmentSettingsForm.freeDeliveryThresholdRequired',
      });
      const freeDeliveryThresholdMin = intl.formatMessage({
        id: 'FulfilmentSettingsForm.freeDeliveryThresholdMin',
      });

      const currencyConfig = appSettings.getCurrencyFormatting(marketplaceCurrency);
      const deliveryOptions = values.deliveryOptions || [];
      const farmCollectionEnabled = deliveryOptions.includes('farmCollection');
      const localDeliveryEnabled = deliveryOptions.includes('localDelivery');
      const courierDeliveryEnabled = deliveryOptions.includes('courierDelivery');
      const freeDeliveryEnabled = !!values.freeDeliveryEnabled;
      const submitDisabled = invalid || disabled || inProgress || pristine;
      const submitReady = ready;
      const classes = classNames(css.root, className);

      const validateLocation = farmCollectionEnabled
        ? composeValidators(
            autocompleteSearchRequired(addressRequiredMessage),
            autocompletePlaceSelected(addressNotRecognizedMessage)
          )
        : () => {};

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          {ready ? (
            <p className={css.success}>
              <FormattedMessage id="FulfilmentSettingsForm.saveSuccess" />
            </p>
          ) : null}

          {saveError ? (
            <p className={css.error}>
              <FormattedMessage id="FulfilmentSettingsForm.saveError" />
            </p>
          ) : null}

          <div className={css.sectionLabel}>
            <FormattedMessage id="FulfilmentSettingsForm.deliveryOptionsLabel" />
          </div>

          <FieldCheckbox
            id={`${formId}.farmCollection`}
            className={css.deliveryCheckbox}
            name="deliveryOptions"
            label={intl.formatMessage({ id: 'FulfilmentSettingsForm.farmCollectionLabel' })}
            value="farmCollection"
          />

          {farmCollectionEnabled ? (
            <>
              <FieldLocationAutocompleteInput
                className={css.input}
                inputClassName={css.locationAutocompleteInput}
                iconClassName={css.locationAutocompleteInputIcon}
                predictionsClassName={css.predictionsRoot}
                validClassName={css.validLocation}
                name="location"
                id={`${formId}.location`}
                label={intl.formatMessage({ id: 'FulfilmentSettingsForm.collectionAddress' })}
                placeholder={intl.formatMessage({ id: 'FulfilmentSettingsForm.addressPlaceholder' })}
                useDefaultPredictions={false}
                format={identity}
                valueFromForm={values.location}
                validate={validateLocation}
              />

              <FieldTextInput
                className={css.input}
                type="text"
                name="building"
                id={`${formId}.building`}
                label={intl.formatMessage(
                  { id: 'FulfilmentSettingsForm.building' },
                  { optionalText }
                )}
                placeholder={intl.formatMessage({
                  id: 'FulfilmentSettingsForm.buildingPlaceholder',
                })}
              />
            </>
          ) : null}

          <FieldCheckbox
            id={`${formId}.localDelivery`}
            className={css.deliveryCheckbox}
            name="deliveryOptions"
            label={intl.formatMessage({ id: 'FulfilmentSettingsForm.localDeliveryIfOfferedLabel' })}
            value="localDelivery"
          />

          {localDeliveryEnabled ? (
            <>
              <FieldCurrencyInput
                id={`${formId}.localDeliveryFeeInSubunits`}
                name="localDeliveryFeeInSubunits"
                className={css.input}
                label={intl.formatMessage({ id: 'FulfilmentSettingsForm.localDeliveryFeeLabel' })}
                placeholder={intl.formatMessage({
                  id: 'FulfilmentSettingsForm.localDeliveryFeePlaceholder',
                })}
                currencyConfig={currencyConfig}
                validate={composeValidators(
                  required(localDeliveryFeeRequired),
                  moneySubUnitAmountAtLeast(localDeliveryFeeRequired, 0)
                )}
              />

              <FieldTextInput
                className={css.input}
                type="textarea"
                name="localDeliveryNotes"
                id={`${formId}.localDeliveryNotes`}
                label={intl.formatMessage(
                  { id: 'FulfilmentSettingsForm.localDeliveryNotesLabel' },
                  { optionalText }
                )}
                placeholder={intl.formatMessage({
                  id: 'FulfilmentSettingsForm.localDeliveryNotesPlaceholder',
                })}
              />

              <FieldTextInput
                id={`${formId}.localDeliveryRadiusInMiles`}
                name="localDeliveryRadiusInMiles"
                className={css.input}
                type="number"
                step="1"
                min={0}
                max={1100}
                parse={value => {
                  const parsed = Number.parseInt(value, 10);
                  return Number.isNaN(parsed) ? null : parsed;
                }}
                label={intl.formatMessage({ id: 'FulfilmentSettingsForm.localDeliveryRadiusLabel' })}
                placeholder={intl.formatMessage({
                  id: 'FulfilmentSettingsForm.localDeliveryRadiusPlaceholder',
                })}
                validate={composeValidators(
                  required(localDeliveryRadiusRequired),
                  value => validateInteger(value, 1100, 0, localDeliveryRadiusMin, localDeliveryRadiusMax)
                )}
                onWheel={e => {
                  if (e.target === document.activeElement) {
                    e.target.blur();
                    setTimeout(() => {
                      e.target.focus();
                    }, 0);
                  }
                }}
              />
            </>
          ) : null}

          <FieldCheckbox
            id={`${formId}.courierDelivery`}
            className={css.deliveryCheckbox}
            name="deliveryOptions"
            label={intl.formatMessage({ id: 'FulfilmentSettingsForm.courierDeliveryLabel' })}
            value="courierDelivery"
          />

          {courierDeliveryEnabled ? (
            <>
              <FieldCurrencyInput
                id={`${formId}.courierDeliveryFeeInSubunits`}
                name="courierDeliveryFeeInSubunits"
                className={css.input}
                label={intl.formatMessage({ id: 'FulfilmentSettingsForm.courierDeliveryFeeLabel' })}
                placeholder={intl.formatMessage({
                  id: 'FulfilmentSettingsForm.courierDeliveryFeePlaceholder',
                })}
                currencyConfig={currencyConfig}
                validate={composeValidators(
                  required(courierDeliveryFeeRequired),
                  moneySubUnitAmountAtLeast(courierDeliveryFeeRequired, 0)
                )}
              />

              <FieldTextInput
                className={css.input}
                type="textarea"
                name="courierDeliveryNotes"
                id={`${formId}.courierDeliveryNotes`}
                label={intl.formatMessage(
                  { id: 'FulfilmentSettingsForm.courierDeliveryNotesLabel' },
                  { optionalText }
                )}
                placeholder={intl.formatMessage({
                  id: 'FulfilmentSettingsForm.courierDeliveryNotesPlaceholder',
                })}
              />
            </>
          ) : null}

          <ValidationError
            fieldMeta={{
              touched: submitFailed,
              error: errors?.deliveryOptions ? deliveryOptionsRequiredMessage : null,
            }}
          />

          <FieldCheckbox
            id={`${formId}.freeDeliveryEnabled`}
            className={css.deliveryCheckbox}
            name="freeDeliveryEnabled"
            label={intl.formatMessage({ id: 'FulfilmentSettingsForm.freeDeliveryEnabled' })}
          />

          {freeDeliveryEnabled ? (
            <FieldCurrencyInput
              id={`${formId}.freeDeliveryThreshold`}
              name="freeDeliveryThreshold"
              className={css.input}
              label={intl.formatMessage({ id: 'FulfilmentSettingsForm.freeDeliveryThreshold' })}
              placeholder={intl.formatMessage({
                id: 'FulfilmentSettingsForm.freeDeliveryThresholdPlaceholder',
              })}
              currencyConfig={currencyConfig}
              validate={composeValidators(
                required(freeDeliveryThresholdRequired),
                moneySubUnitAmountAtLeast(freeDeliveryThresholdMin, 1)
              )}
            />
          ) : null}

          <Button
            className={css.submitButton}
            type="submit"
            inProgress={inProgress}
            disabled={submitDisabled}
            ready={submitReady}
          >
            {saveActionMsg}
          </Button>
        </Form>
      );
    }}
  />
);

export default FulfilmentSettingsForm;
